import { ID, IDPrefixes, NewTagWithID } from '@griply/types';
import { defaultTagColorOption } from '@griply/utils';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'shared/assets/icons';
import { TagSearchMenu } from 'shared/components/ui/tag-search-menu';
import { useSelectedRelationShipIdsById } from 'shared/hooks/use-relationships';

import { useTaskDetailShortcut } from '../../hooks/use-task-detail-shortcut';
import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { useAddTagToCreate, useData } from '../../use-data';
import { MetaInteraction } from '../meta-interaction';

export type MetaTagsProps = {
  taskId?: ID;
  tagIds?: ID[];
  isCreate?: boolean;
  onChange: (relationTypeIds: ID[], newTags?: NewTagWithID[]) => void;
};

export const MetaTags: React.FC<MetaTagsProps> = ({
  taskId,
  tagIds,
  isCreate,
  onChange,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const { t } = useTranslation();
  const { tags, createTag, relationships } = useData();
  const addTagToCreate = useAddTagToCreate();
  const { tag: taskTagIds } = useSelectedRelationShipIdsById(
    taskId,
    IDPrefixes.Task,
    relationships,
  );

  const _tagIds = tagIds ?? taskTagIds;

  const _onCreate = async (name: string) => {
    if (!name) {
      return;
    }

    // A task being created holds its tags as name tokens, so the staged tag travels along.
    if (isCreate) {
      const stagedTag = addTagToCreate(name);
      onChange([..._tagIds, stagedTag.id], [stagedTag]);
      return;
    }

    if (!createTag) {
      return;
    }

    const newTag = await createTag({
      name,
      color: defaultTagColorOption.value,
    });
    onChange([..._tagIds, newTag.id]);
  };

  const onShortcut = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger if Shift is pressed (as that might be another shortcut)
      if (e.shiftKey) {
        return;
      }

      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openMenu();
      }
    },
    [menuOpen, openMenu],
  );

  useTaskDetailShortcut(['KeyT'], onShortcut, {
    ignoreInputFields: true,
    capture: true,
  });

  const tagLabels = useMemo(() => {
    const filteredTags = _tagIds?.length
      ? tags.filter((tag) => _tagIds?.includes(tag.id))
      : [];
    return filteredTags.length
      ? filteredTags.map(({ name }) => name).join(', ')
      : undefined;
  }, [_tagIds, tags]);

  const onClear = () => {
    onChange([]);
    closeMenu();
  };

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={Tag}
        placeholder={t('dialogs.task-detail.meta.tags.placeholder')}
        label={tagLabels}
        onClick={openMenu}
        selected={menuOpen}
        onClear={tagLabels ? onClear : undefined}
      />

      {menuOpen && (
        <TagSearchMenu
          tags={tags}
          onChange={onChange}
          value={_tagIds}
          referenceElement={buttonRef}
          onCreate={isCreate || createTag ? _onCreate : undefined}
          onOutside={closeMenu}
          hideCreateButton
        />
      )}
    </>
  );
};
